// ARCHIVO: src/pages/Ayuda.jsx
// PÁGINA: Ayuda del Sistema - Guía completa de funcionalidades
// FUNCIONALIDAD: Explicación detallada de cada opción del menú

import React, { useState } from 'react';
import { 
  Home, 
  Package, 
  AlertTriangle, 
  XCircle, 
  ShoppingCart, 
  ClipboardList, 
  Settings, 
  Store,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Info,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const Ayuda = () => {
  const [seccionesAbiertas, setSeccionesAbiertas] = useState({});

  const toggleSeccion = (seccion) => {
    setSeccionesAbiertas(prev => ({
      ...prev,
      [seccion]: !prev[seccion]
    }));
  };

  const SeccionAyuda = ({ titulo, icono, children, id }) => {
    const abierta = seccionesAbiertas[id];
    
    return (
      <div className="seccion-ayuda">
        <button 
          className="seccion-titulo"
          onClick={() => toggleSeccion(id)}
        >
          {icono}
          <span>{titulo}</span>
          {abierta ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>
        
        {abierta && (
          <div className="seccion-contenido">
            {children}
          </div>
        )}
      </div>
    );
  };

  const ItemAyuda = ({ titulo, descripcion, caracteristicas = [], advertencias = [], consejos = [] }) => (
    <div className="item-ayuda">
      <h4 className="item-titulo">{titulo}</h4>
      <p className="item-descripcion">{descripcion}</p>
      
      {caracteristicas.length > 0 && (
        <div className="item-lista">
          <h5><CheckCircle size={16} /> Características:</h5>
          <ul>
            {caracteristicas.map((caracteristica, index) => (
              <li key={index}>{caracteristica}</li>
            ))}
          </ul>
        </div>
      )}
      
      {advertencias.length > 0 && (
        <div className="item-lista advertencias">
          <h5><AlertCircle size={16} /> Advertencias:</h5>
          <ul>
            {advertencias.map((advertencia, index) => (
              <li key={index}>{advertencia}</li>
            ))}
          </ul>
        </div>
      )}
      
      {consejos.length > 0 && (
        <div className="item-lista consejos">
          <h5><Info size={16} /> Consejos:</h5>
          <ul>
            {consejos.map((consejo, index) => (
              <li key={index}>{consejo}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div className="ayuda-container">
      <div className="ayuda-header">
        <div className="ayuda-titulo">
          <HelpCircle size={32} />
          <h1>Centro de Ayuda - SinFilas v3.0</h1>
        </div>
        <p className="ayuda-subtitulo">
          Guía completa para utilizar todas las funcionalidades del sistema de ventas
        </p>
      </div>

      <div className="ayuda-contenido">
        <SeccionAyuda 
          titulo="Dashboard - Panel Principal" 
          icono={<Home size={24} />}
          id="dashboard"
        >
          <ItemAyuda
            titulo="¿Qué es el Dashboard?"
            descripcion="El Dashboard es tu centro de control principal donde puedes ver un resumen completo de tu comercio y acceder rápidamente a las funciones más importantes."
            caracteristicas={[
              "Muestra estadísticas generales de tu comercio",
              "Visualiza el logo de tu comercio",
              "Información básica del comercio (nombre, descripción)",
              "Acceso rápido a funciones principales"
            ]}
            consejos={[
              "Revisa regularmente las estadísticas para mantenerte informado",
              "El logo se actualiza automáticamente cuando lo cambias en Configuración",
              "Las estadísticas se actualizan en tiempo real"
            ]}
          />
        </SeccionAyuda>

        <SeccionAyuda 
          titulo="Productos - Gestión de Inventario" 
          icono={<Package size={24} />}
          id="productos"
        >
          <ItemAyuda
            titulo="¿Qué puedes hacer en Productos?"
            descripcion="Esta sección te permite gestionar completamente tu inventario de productos, desde agregar nuevos productos hasta modificar existentes."
            caracteristicas={[
              "Ver todos los productos de tu comercio",
              "Buscar productos por nombre, código, marca o categoría",
              "Filtrar por categoría y clasificación dietética",
              "Agregar nuevos productos con información completa",
              "Editar productos existentes",
              "Eliminar productos que ya no vendes",
              "Ver imágenes de productos",
              "Gestionar precios de venta y compra",
              "Controlar stock actual y mínimo"
            ]}
            advertencias={[
              "Al eliminar un producto, se eliminará permanentemente",
              "Los cambios en precios afectan inmediatamente las ventas",
              "El stock mínimo es importante para las alertas de Stock Bajo"
            ]}
            consejos={[
              "Usa códigos de barras únicos para evitar duplicados",
              "Mantén actualizada la información de precios",
              "Sube imágenes de buena calidad para mejor presentación",
              "Establece stock mínimo apropiado para cada producto",
              "Usa las clasificaciones dietéticas para mejor organización"
            ]}
          />
        </SeccionAyuda>

        <SeccionAyuda 
          titulo="Stock Bajo - Productos con Poco Stock" 
          icono={<AlertTriangle size={24} />}
          id="stock-bajo"
        >
          <ItemAyuda
            titulo="¿Qué es Stock Bajo?"
            descripcion="Esta sección te muestra todos los productos que están por debajo del stock mínimo establecido, ayudándote a evitar quedarte sin productos."
            caracteristicas={[
              "Lista automática de productos con stock bajo",
              "Muestra la diferencia entre stock actual y mínimo",
              "Indicadores visuales de urgencia",
              "Acceso directo para editar stock",
              "Información de precios y categorías"
            ]}
            advertencias={[
              "Revisa esta sección regularmente para evitar faltantes",
              "Los productos con stock bajo pueden afectar las ventas",
              "Actualiza el stock mínimo según la demanda real"
            ]}
            consejos={[
              "Revisa esta sección diariamente",
              "Establece stock mínimo basado en la velocidad de venta",
              "Considera hacer pedidos de reposición cuando veas productos aquí",
              "Usa esta información para planificar compras"
            ]}
          />
        </SeccionAyuda>

        <SeccionAyuda 
          titulo="Agotados - Productos Sin Stock" 
          icono={<XCircle size={24} />}
          id="agotados"
        >
          <ItemAyuda
            titulo="¿Qué son los Productos Agotados?"
            descripcion="Esta sección muestra todos los productos que tienen stock en cero, es decir, que ya no tienes disponibles para la venta."
            caracteristicas={[
              "Lista de productos con stock = 0",
              "Información completa de cada producto",
              "Acceso para reponer stock",
              "Historial de productos agotados"
            ]}
            advertencias={[
              "Los productos agotados no se pueden vender",
              "Esto puede causar pérdida de ventas",
              "Algunos productos pueden tener stock negativo por ventas pendientes"
            ]}
            consejos={[
              "Revisa esta sección inmediatamente",
              "Prioriza la reposición de productos más vendidos",
              "Considera desactivar productos que ya no vendes",
              "Mantén comunicación con proveedores para reposición rápida"
            ]}
          />
        </SeccionAyuda>

        <SeccionAyuda 
          titulo="Ventas - Gestión de Ventas" 
          icono={<ShoppingCart size={24} />}
          id="ventas"
        >
          <ItemAyuda
            titulo="¿Qué puedes hacer en Ventas?"
            descripcion="Esta sección te permite gestionar todas las ventas de tu comercio, desde registrar nuevas ventas hasta consultar el historial."
            caracteristicas={[
              "Registrar nuevas ventas",
              "Ver historial de ventas",
              "Calcular totales automáticamente",
              "Generar tickets de venta",
              "Consultar ventas por fecha",
              "Estadísticas de ventas"
            ]}
            advertencias={[
              "Las ventas registradas no se pueden modificar fácilmente",
              "Verifica los datos antes de confirmar la venta",
              "Mantén respaldo de las ventas importantes"
            ]}
            consejos={[
              "Registra las ventas inmediatamente después de realizarlas",
              "Revisa regularmente el historial de ventas",
              "Usa las estadísticas para tomar decisiones de negocio",
              "Mantén los tickets de venta para referencia"
            ]}
          />
        </SeccionAyuda>

        <SeccionAyuda 
          titulo="Pedidos - Gestión de Pedidos" 
          icono={<ClipboardList size={24} />}
          id="pedidos"
        >
          <ItemAyuda
            titulo="¿Qué son los Pedidos?"
            descripcion="Esta sección te permite gestionar pedidos de clientes, tanto presenciales como a distancia, manteniendo un control completo del flujo de trabajo."
            caracteristicas={[
              "Crear nuevos pedidos",
              "Gestionar pedidos pendientes",
              "Marcar pedidos como completados",
              "Seguimiento del estado de pedidos",
              "Información del cliente"
            ]}
            advertencias={[
              "Los pedidos pendientes pueden afectar el stock disponible",
              "Mantén actualizado el estado de los pedidos",
              "Comunica cambios de estado a los clientes"
            ]}
            consejos={[
              "Procesa los pedidos en orden de llegada",
              "Mantén comunicación clara con los clientes",
              "Actualiza el estado de los pedidos regularmente",
              "Usa esta sección para planificar la producción"
            ]}
          />
        </SeccionAyuda>

        <SeccionAyuda 
          titulo="Configuración - Ajustes del Sistema" 
          icono={<Settings size={24} />}
          id="configuracion"
        >
          <ItemAyuda
            titulo="¿Qué puedes configurar?"
            descripcion="La sección de configuración te permite personalizar el sistema según las necesidades específicas de tu comercio."
            caracteristicas={[
              "Perfil del Comercio (datos básicos, logo, contacto)",
              "Configuración de ventas",
              "Ajustes de stock",
              "Configuración de impresión",
              "Configuración de PWA (aplicación móvil)"
            ]}
            advertencias={[
              "Los cambios en configuración afectan todo el sistema",
              "Haz respaldo antes de cambios importantes",
              "Algunos cambios requieren reiniciar la aplicación"
            ]}
            consejos={[
              "Configura primero los datos básicos de tu comercio",
              "Sube un logo de buena calidad",
              "Revisa la configuración regularmente",
              "Mantén actualizada la información de contacto"
            ]}
          />
        </SeccionAyuda>

        <SeccionAyuda 
          titulo="Perfil del Comercio - Información Personal" 
          icono={<Store size={24} />}
          id="perfil"
        >
          <ItemAyuda
            titulo="¿Qué es el Perfil del Comercio?"
            descripcion="Esta sección contiene toda la información personal y fiscal de tu comercio, incluyendo datos de contacto, información fiscal y configuración visual."
            caracteristicas={[
              "Datos básicos (nombre de fantasía, razón social)",
              "Logo personalizado del comercio",
              "Información de contacto completa",
              "Datos fiscales (CUIT, condición de IVA, IIBB)",
              "Dirección y ubicación",
              "Configuración de ventas"
            ]}
            advertencias={[
              "Los datos fiscales deben ser exactos para facturación",
              "El logo se muestra en tickets e informes",
              "Los cambios se aplican inmediatamente"
            ]}
            consejos={[
              "Mantén actualizada la información de contacto",
              "Usa un logo de alta calidad y formato apropiado",
              "Verifica que los datos fiscales sean correctos",
              "Guarda los cambios después de cada modificación"
            ]}
          />
        </SeccionAyuda>

        <div className="ayuda-footer">
          <div className="ayuda-contacto">
            <h3>¿Necesitas más ayuda?</h3>
            <p>
              Si tienes preguntas específicas o encuentras algún problema, 
              contacta al equipo de soporte técnico.
            </p>
            <div className="ayuda-info">
              <p><strong>Sistema:</strong> SinFilas v3.0</p>
              <p><strong>Última actualización:</strong> Septiembre 2025</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ayuda;




